// ── schema.org/Recipe structured data ────────────────────────────────
// Emitted on public recipe pages so other apps can read a shared link.
// The immediate motivation is Grocery's Safari share extension, which imports
// a recipe's ingredients from a web page — but schema.org/Recipe is the format
// AnyList, Paprika, Mela and Google all read, so one tag serves all of them.
//
// Amounts are rendered at the recipe's own yield, not whatever the viewer has
// the servings stepper set to: the structured data describes the recipe, and a
// scraper that picked up a doubled batch because a visitor was mid-tinker would
// be quietly wrong.

import { fmtQty, parseQty } from "./format";
import type { Recipe } from "./types";

/** "2 cloves garlic", "500 g chicken thighs", "salt" — the same shape the
 *  ingredient rows read, flattened into one string per schema.org. */
export function ingredientLine(ing: {
  base?: number | string;
  unit?: string;
  name: string;
  note?: string;
}): string {
  const parsed =
    ing.base == null || ing.base === ""
      ? null
      : typeof ing.base === "number"
        ? ing.base
        : parseQty(String(ing.base));
  const amount = parsed == null ? "" : fmtQty(parsed);
  const unit = (ing.unit ?? "").trim();
  return [amount, unit, ing.name].filter(Boolean).join(" ").trim();
}

/** Minutes → ISO 8601 duration, which is what schema.org expects. */
function isoDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `PT${h > 0 ? `${h}H` : ""}${m > 0 || h === 0 ? `${m}M` : ""}`;
}

export function buildRecipeJsonLd(
  recipe: Recipe,
  opts: { url?: string; authorName?: string } = {}
): Record<string, unknown> {
  const ingredients = (recipe.ingredientGroups ?? [])
    .flatMap((g) => g.ingredients ?? [])
    .map(ingredientLine)
    .filter(Boolean);

  const steps = (recipe.steps ?? [])
    .map((s) => {
      const text = [s.text, ...(s.bullets ?? [])].filter(Boolean).join(" ");
      if (!text && !s.title) return null;
      return {
        "@type": "HowToStep",
        ...(s.title ? { name: s.title } : {}),
        text: text || s.title,
      };
    })
    .filter(Boolean);

  // Timers are the only duration the app records, so total cook time is their
  // sum — omitted entirely rather than guessed when no step has one.
  const timerSeconds = (recipe.steps ?? []).reduce((n, s) => n + (s.timer ?? 0), 0);

  const m = recipe.macros;
  const nutrition = m
    ? {
        "@type": "NutritionInformation",
        ...(m.calories != null ? { calories: `${m.calories} kcal` } : {}),
        ...(m.protein != null ? { proteinContent: `${m.protein} g` } : {}),
        ...(m.carbs != null ? { carbohydrateContent: `${m.carbs} g` } : {}),
        ...(m.fat != null ? { fatContent: `${m.fat} g` } : {}),
        ...(m.fiber != null ? { fiberContent: `${m.fiber} g` } : {}),
      }
    : undefined;

  return {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.title,
    ...(recipe.description ? { description: recipe.description } : {}),
    ...(recipe.imageUrl ? { image: [recipe.imageUrl] } : {}),
    ...(opts.url ? { url: opts.url } : {}),
    ...(opts.authorName
      ? { author: { "@type": "Person", name: opts.authorName } }
      : {}),
    ...(recipe.category ? { recipeCategory: recipe.category } : {}),
    ...(recipe.tags && recipe.tags.length ? { keywords: recipe.tags.join(", ") } : {}),
    ...(recipe.servings
      ? {
          recipeYield: `${recipe.servings} serving${recipe.servings === 1 ? "" : "s"}`,
        }
      : {}),
    ...(timerSeconds > 0 ? { totalTime: isoDuration(Math.round(timerSeconds / 60)) } : {}),
    ...(ingredients.length ? { recipeIngredient: ingredients } : {}),
    ...(steps.length ? { recipeInstructions: steps } : {}),
    // Macros are stored per serving, which is exactly what schema.org means by
    // nutrition — no conversion needed.
    ...(nutrition && Object.keys(nutrition).length > 1 ? { nutrition } : {}),
  };
}

/**
 * Put the recipe's structured data in <head>, and take it away again on
 * unmount — a stale tag describing the previous recipe is worse than none,
 * since a share extension would import the wrong ingredients.
 */
export function mountRecipeJsonLd(json: Record<string, unknown>): () => void {
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.dataset.recipeJsonld = "true";
  el.textContent = JSON.stringify(json);
  document.head.appendChild(el);
  return () => {
    el.remove();
  };
}
